import React, { useCallback, useEffect, useMemo } from "react";
import { makeVar, useReactiveVar } from "@apollo/client";

import { config, IS_LOCAL_DEVELOPMENT } from "./config";
import { safePosthogFeatureFlags } from "lib/analytics/postHog";

import { useAppRouter } from "./useAppRouter";
import { Route } from "./routes";
import { useSelf } from "domains/user/hooks/useSelf";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { useThirdPartyLogin } from "components/partners/useThirdPartyLogin";
import { PRE_EXISTING_ACCOUNT_PARAM } from "components/intro/partner/shared";
import { getPostLoginRedirectUrl, savePostLoginRedirectUrl, setWelcomeTourCookie } from "./cookies";
import { useSearchParams } from "next/navigation";
import useRouteAccess from "./useRouteAccess";
import { useCustomerMappingQuery } from "components/monetization/graphql.generated";
import { checkSubscriptionPause, initChurnkey } from "./customerSupport/churnkey";

export const FROM_REGISTER_PARTNER = "registerPartner";
export const SOURCE_PARAM = "source";

type VerifyRouteAccessProviderProps = {
  children: React.ReactNode;
};

export const loadingFeatureFlagsVar = makeVar(Boolean(!config.IS_TEST && config.POSTHOG_KEY));

/**
 * Wraps children in route protection, ensuring that the current user has access
 * to the requested resource before rendering it. Redirects appropriately if the user does not
 * have access
 *
 * The idea for this is almost there, but it needs some work for sure. Such as - how to work with the
 * fact that using this requires apollo client? Should all routes use apollo client? Do I need
 * to update this so it somehow doesn't require apollo client (no idea how to do that off top of head)
 *
 * do I make different boundaries? Like a Public, Private, Company, Self, etc boundary?
 *
 * Also, path identification - there isn't a clear way to get from the info
 * provided from next to the path we care about. currently it's a bit hardcoded.
 *
 */
export const VerifyRouteAccessProvider = ({ children }: VerifyRouteAccessProviderProps) => {
  // TODO tests need to wrap with UserProvider; this is a quicker bailout than self; maybe save for middleware
  // const { user, isLoading: isLoadingAuth0User } = useUser();
  const searchParams = useSearchParams();
  const { self, loading: _loadingSelf } = useSelf();
  const loadingFeatureFlags = useReactiveVar(loadingFeatureFlagsVar);
  const loadingSelf = _loadingSelf || loadingFeatureFlags;
  const {
    company,
    loading: loadingCompany,
    completedOnboarding,
    membershipRole,
  } = useActiveCompany();
  const { isApiPartner, partnerOnboardingId, apiPartnerAuthType } = useThirdPartyLogin();
  const {
    router,
    goHome,
    goToLogin,
    goToIntro,
    isUnknownRoute,
    getCurrentRoute,
    goToConnectSuccess,
    goToConnectPartnerDataSource,
    goToNoAccess,
  } = useAppRouter();
  const { hasAccess } = useRouteAccess();
  const forceNewTour = (router.query.newTour as string) === "true";
  const fromLogin = (router.query.source as string) === "login";
  const fromRegisterPartnerUser = (router.query.source as string) === FROM_REGISTER_PARTNER;

  const POSTHOG_LOADING_DELAY = 1000;

  // We want to avoid making routing decisions during transitional loading states
  // So we are looking for "stable states"
  const isInStableState = useMemo(() => {
    const hasRequiredData = self && company;
    const isNotLoggedIn = !loadingSelf && !self;

    // User exists, companies have finished loading, but no company
    const userExistsWithCompaniesLoaded = !loadingSelf && self && !loadingCompany && !company;

    // We're in a stable state if:
    if (hasRequiredData) return true;
    if (isNotLoggedIn) return true;
    if (userExistsWithCompaniesLoaded) return true;

    // Otherwise, we're in an unstable transition state and we should not redirect yet
    return false;
  }, [loadingSelf, loadingCompany, self, company]);

  const { data: customerMapping } = useCustomerMappingQuery({
    variables: { id: company?.id ?? "" },
    skip: !company?.id,
  });

  // load churnkey script
  useEffect(() => {
    initChurnkey();
  }, []);

  // Check subscription pause status when company is loaded
  useEffect(() => {
    const stripeCustomerId = customerMapping?.customerMapping?.stripeCustomerId;
    if (stripeCustomerId && typeof stripeCustomerId === "string") {
      checkSubscriptionPause(stripeCustomerId);
    }
  }, [company, loadingCompany, customerMapping]);

  // We need to persist these until onboarding is completed
  const saveReferralParameters = useCallback(() => {
    const promoCode = searchParams?.get("promocode");
    if (promoCode) {
      // Not the best. User can finish the onboarding on another device.
      // But will work for now.
      localStorage.setItem("promoCode", promoCode);
    }

    // Referral ID from Impact.com
    const irclickid = searchParams?.get("irclickid");
    if (irclickid) {
      localStorage.setItem("irclickid", irclickid);
    }

    // Marketing ID from Facebook
    const fbc = searchParams?.get("fbc");
    if (fbc) {
      localStorage.setItem("fbc", fbc);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!self || !loadingFeatureFlags) {
      return;
    }

    safePosthogFeatureFlags()?.onFeatureFlags(() => loadingFeatureFlagsVar(false));

    const timeout = setTimeout(() => loadingFeatureFlagsVar(false), POSTHOG_LOADING_DELAY);
    return () => clearTimeout(timeout);
  }, [loadingFeatureFlags, self]);

  useEffect(() => {
    if (forceNewTour) {
      setWelcomeTourCookie(true);
    }
  }, [forceNewTour]);

  const path = getCurrentRoute();
  const hasAccessToPath = hasAccess(path);

  /*
    TODO: refactor below into a state machine someday
  */

  // returns string for debugging convenience - you can
  // log which redirect is being hit
  const willRedirect = useMemo(() => {
    if (!isInStableState) {
      return null; // Don't redirect unless we're in a stable state
    }

    const getIntroRedirect = (): "intro" | null => {
      const { companyId } = router.query;
      const isHotReload = IS_LOCAL_DEVELOPMENT && Boolean(companyId);

      /*
        We are determining a hot reload by these 2 conditions:
        1. We are in local development
        2. The companyId is present in the query params (you've already logged in successfully)

        This works because even though hot reloads wipe out all state,
        we have preserved it in the URL.
      */

      if (isHotReload) {
        return null; // don't redirect
      }

      return "intro"; // for everything else, redirect to intro
    };

    const isPartnerUserComingFromAuth =
      !loadingSelf &&
      apiPartnerAuthType &&
      completedOnboarding &&
      isApiPartner &&
      (fromRegisterPartnerUser || fromLogin);
    const userHasNoAccessToCurrentPath = !loadingSelf && !loadingCompany && !hasAccessToPath;
    const shouldRedirectToPartnerDataSource =
      self &&
      company &&
      hasAccessToPath &&
      partnerOnboardingId &&
      path !== Route.connectPartnerDataSource;
    const userHasNoAccessRole =
      !loadingSelf && !loadingCompany && membershipRole === "None" && path !== Route.noAccess;
    const isNewUserNeedingOnboarding =
      !loadingSelf &&
      !loadingCompany &&
      self &&
      !company &&
      path !== Route.intro &&
      !completedOnboarding;

    // USER DOES NOT HAVE ACCESS TO THEIR CURRENT PATH
    if (userHasNoAccessToCurrentPath) {
      if (!self) return "login";

      if (!completedOnboarding || !company) {
        return getIntroRedirect();
      }

      return "home"; // must redirect someplace safe, can't stay where they are
    } else {
      // USER HAS ACCESS TO THEIR CURRENT PATH

      if (isUnknownRoute(path)) {
        return "home";
      }

      if (isNewUserNeedingOnboarding) {
        return getIntroRedirect();
      }

      // TODO - registerStripeUser should go to connect success if the user already has a company
      // will need to modify the response of auth-stripe/sync
      // it should only redirect here if they are coming directly from having logged in
      // and have a preexisting puzzle account
      if (isPartnerUserComingFromAuth) {
        return "connect_success";
      }

      if (shouldRedirectToPartnerDataSource) {
        return "connect_partner_data_source";
      }

      if (userHasNoAccessRole) {
        return "no_access";
      }

      return null; // the user has access to their current path, they can stay where they are
    }
  }, [
    isInStableState,
    isUnknownRoute,
    path,
    loadingSelf,
    apiPartnerAuthType,
    completedOnboarding,
    isApiPartner,
    fromRegisterPartnerUser,
    fromLogin,
    loadingCompany,
    hasAccessToPath,
    self,
    company,
    partnerOnboardingId,
    membershipRole,
    router.query,
  ]);

  useEffect(() => {
    if (willRedirect === "login") {
      const redirectUrl = getPostLoginRedirectUrl();
      if (!redirectUrl || !redirectUrl.startsWith(path)) {
        const searchParams = new URLSearchParams(router.query as Record<string, string>);
        const redirectUrl = path + (searchParams ? `?${searchParams}` : "");
        savePostLoginRedirectUrl(redirectUrl);
      }
    }
  }, [path, router.query, willRedirect]);

  useEffect(() => {
    switch (willRedirect) {
      case "intro":
        saveReferralParameters();
        return goToIntro();
      case "home":
        return goHome();
      case "connect_success":
        return goToConnectSuccess({
          query: { [PRE_EXISTING_ACCOUNT_PARAM]: true },
        });
      case "login":
        return goToLogin();
      case "connect_partner_data_source":
        return goToConnectPartnerDataSource();
      case "no_access":
        return goToNoAccess();
    }
  }, [
    goHome,
    goToConnectSuccess,
    goToIntro,
    goToLogin,
    willRedirect,
    saveReferralParameters,
    goToConnectPartnerDataSource,
    goToNoAccess,
  ]);

  return <>{hasAccessToPath && !willRedirect && children}</>;
};
